import { MinusCircleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Radio, Select, Grid } from 'antd';
import { Rule } from 'antd/es/form';
import { useState } from 'react';
import {
  ApplicantProfileContactEditContainerApplicantUserContactInformationFieldsFragment,
  ApplicantUserContactInformationInput,
  ApplicantUserPhoneInput
} from '../../../../generated';
import { Country } from '../../../../types/country';
import { StateSelect } from '../shared/components/state-select';
import { EditProfile } from './edit-profile';

interface SelectOption {
  label: string;
  value: string;
}

const phoneTypes = [
  {
    label: 'Home',
    value: 'home'
  },
  {
    label: 'Work',
    value: 'work'
  },
  {
    label: 'Cell',
    value: 'cell'
  },
  {
    label: 'Other',
    value: 'other'
  }
];

interface AdditionalPhone {
  phoneType: string;
  phoneNumber: string;
}

interface ProfileContactEditProps {
  contactInformation: ApplicantProfileContactEditContainerApplicantUserContactInformationFieldsFragment;
  onCancel: () => void;
  onSave: (contact: ApplicantUserContactInformationInput) => Promise<void>;
  countryData: Country[];
}

export const ProfileContactEdit: React.FC<ProfileContactEditProps> = (props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm<ApplicantUserContactInformationInput>();
  const [zipCodeRules, setZipCodeRules] = useState<Rule[]>([{ required: false, message: '' }]);
  const selectedCountry = Form.useWatch(['address', 'country'], form);

  const onCountryChange = (value: string) => {
    // Reset State/Province field when country changes
    form.setFieldsValue({
      address: {
        stateOrProvince: undefined
      }
    });

    switch (value) {
      case 'US':
      case 'CA':
        setZipCodeRules([
          { required: true, message: 'Please enter the ZIP/Postal code' },
          {
            pattern: /^\d{5}(?:[-\s]\d{4})?$/,
            message: 'Please enter a valid ZIP/Postal code'
          }
        ]);
        break;
      default:
        setZipCodeRules([{ required: false, message: '' }]);
        break;
    }
  };

  const [selectablePhoneTypes, setSelectablePhoneTypes] = useState<SelectOption[]>(phoneTypes);
  const [additionalPhone, setAdditionalPhone] = useState<AdditionalPhone[]>([]);
  const onPhoneSelectChanged = (values: string, index: number) => {
    const newAdditionalPhone = [...additionalPhone];
    newAdditionalPhone[index].phoneType = values;
    newAdditionalPhone[index].phoneNumber = '';

    const selectedPhoneTypes: string[] = [];
    newAdditionalPhone.forEach((phone: AdditionalPhone) => {
      selectedPhoneTypes.push(phone.phoneType);
    });

    const remainingPhoneTypes = selectablePhoneTypes.filter((type: SelectOption) => !selectedPhoneTypes.includes(type.value));
    setSelectablePhoneTypes(remainingPhoneTypes);
    setAdditionalPhone(newAdditionalPhone);
  };

  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;

  const ContactDetailsForm = (
    <Form
      style={{ width: '100%' }}
      form={form}
      initialValues={props?.contactInformation}
      layout="vertical"
      onFinish={async (values) => {
        const contactInfo: ApplicantUserContactInformationInput = {
          address: values.address
        };
        contactInfo.phone = values.phone
          ?.filter((phone): phone is ApplicantUserPhoneInput => phone !== null)
          .map((phone: ApplicantUserPhoneInput) => {
            return {
              id: phone.id as string,
              phoneType: phone.phoneType,
              phoneNumber: phone.phoneNumber,
              isPrimary: phone.isPrimary
            };
          });

        contactInfo.emailAddress = values?.emailAddress;
        props?.onSave(contactInfo);
        setIsSubmitting(true);
      }}
    >
      <p className="ant-form-item-label">Phone Numbers</p>
      <Form.List name={['phone']}>
        {(fields, { add, remove }) => {
          return (
            <div>
              {fields.map((field, index) => {
                return (
                  <div key={field.key} style={{ marginBottom: '15px', display: 'flex' }}>
                    <div
                      style={{
                        display: 'inline-block',
                        verticalAlign: 'top',
                        paddingRight: '10px'
                      }}
                    >
                      <MinusCircleOutlined
                        className="dynamic-delete-button"
                        onClick={() => {
                          remove(field.name);
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: '10px',
                        paddingTop: '3px',
                        paddingLeft: '10px',
                        borderLeft: '1px solid darkgrey'
                      }}
                    >
                      <Form.Item name={[index, 'id']} hidden={true}>
                        <Input hidden={true} />
                      </Form.Item>
                      <Form.Item
                        name={[index, 'phoneType']}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            message: 'Phone Type is required'
                          }
                        ]}
                        label="Phone Type"
                      >
                        <Select
                          placeholder="Phone Type"
                          style={{ width: 200 }}
                          onChange={(values) => {
                            onPhoneSelectChanged(values, index);
                          }}
                        >
                          {selectablePhoneTypes?.map((item: SelectOption) => (
                            <Select.Option key={item.value} value={item.value}>
                              {item.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name={[index, 'phoneNumber']}
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                          {
                            required: true,
                            message: 'Phone Number is required'
                          }
                        ]}
                        label="Phone Number"
                      >
                        <Input placeholder="Phone Number" maxLength={15} />
                        {/* <FormTags /> */}
                      </Form.Item>
                      <Form.Item name={[index, 'isPrimary']} valuePropName="checked">
                        <Radio
                          onChange={() => {
                            // unchecked all other radio buttons and set the current radio button to true
                            form.setFieldsValue({
                              phone: form.getFieldValue(['phone']).map((p: ApplicantUserPhoneInput, i: number) => {
                                return {
                                  ...p,
                                  isPrimary: i === index
                                };
                              })
                            });
                          }}
                          value="true"
                          style={{
                            marginTop: '-5px',
                            paddingTop: '40px',
                            textAlign: 'justify',
                            width: 'Fixed (92px)',
                            height: 'Hug (22px)',
                            left: '892px',
                            gap: '8px',
                            top: '310px'
                          }}
                        >
                          Primary
                        </Radio>
                      </Form.Item>
                    </div>
                  </div>
                );
              })}
              {fields.length < 5 ? (
                <Form.Item style={{width: "100%"}}>
                  <Button
                    type="dashed"
                    onClick={() => {
                      setAdditionalPhone([...additionalPhone, { phoneType: '', phoneNumber: '' }]);
                      add();
                    }}
                    block
                    style={{ width: "90%", paddingRight: '10px' }}
                  >
                    Add Additional Phone
                  </Button>
                </Form.Item>
              ) : (
                <></>
              )}
            </div>
          );
        }}
      </Form.List>
      <h2>Address of Residence</h2>
      <Form.Item rules={[{ required: true, message: 'Address Line 1 is required' }]} label="Address Line 1" name={['address', 'streetAddressLine1']}>
        <Input style={{ width: "90%" }} placeholder="Address Line:1" />
      </Form.Item>
      <Form.Item label="Address Line 2" name={['address', 'streetAddressLine2']}>
        <Input style={{ width: "90%" }} placeholder="Address Line:2" />
      </Form.Item>
      <Form.Item rules={[{ required: true, message: 'Country is required' }]} name={['address', 'country']} label="Country">
        <Select placeholder="Country" style={isMobile ? {width: 200} : {width: "90%"}} onChange={(value) => onCountryChange(value)}>
          {props?.countryData.map((item: Country) => (
            <Select.Option key={item.countryCode} value={item.countryCode}>
              {item.countryName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <div
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row'
        }}
      >
        <Form.Item label="City" name={['address', 'city']}>
          <Input style={{width: "90%"}} placeholder="City" />
        </Form.Item>
        <StateSelect style={isMobile ? {width: 200} : {width: "90%"}} name={['address', 'stateOrProvince']} selectedCountry={selectedCountry} countryData={props?.countryData} />
        <Form.Item label="Zip / Postal Code" rules={zipCodeRules} name={['address', 'zipOrPostalCode']}>
          <Input style={{ width: "90%" }} maxLength={10} minLength={5} placeholder="Zip / Postal Code" />
        </Form.Item>
      </div>

      <div className="flex items-center justify-end gap-4 w-full mb-16">
        <Button onClick={props?.onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button htmlType="submit" type="primary" value={'save'} loading={isSubmitting}>
          Save
        </Button>
      </div>
    </Form>
  );

  return <EditProfile title="Edit Contact Details" form={ContactDetailsForm} />;
};
